/**
 * A record's revision history. Editors can restore an earlier revision, which submits an
 * ordinary edit (so the restore itself is reviewed and recorded like any change).
 */
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { Undo2 } from 'lucide-react'
import { Avatar, ErrorState, Loading, Time } from '../components/Bits'
import { api, ApiError } from '../lib/api'
import { fieldLabel, TYPE_LABELS } from '../lib/format'
import { invalidate, useQuery } from '../lib/query'
import { useSession } from '../lib/session'
import { useTitle } from '../lib/useTitle'
import type { HistoryItem } from '../lib/types'
import { ENTITY_PATHS, ENTITY_TYPES, docTitle, type AnyDoc, type EntityType } from '../../shared/model'

export function History() {
  const { kind = '', id = '' } = useParams()
  const type = ENTITY_TYPES.find((t) => ENTITY_PATHS[t] === kind) as EntityType
  const navigate = useNavigate()
  const session = useSession()
  const [busy, setBusy] = useState<number | null>(null)
  const [err, setErr] = useState('')
  const { data: rec } = useQuery<{ revision: number; doc: AnyDoc }>(`/api/v1/${kind}/${id}`)
  const { data, error, refresh } = useQuery<{ items: HistoryItem[] }>(`/api/v1/${kind}/${id}/history`)
  const title = rec ? docTitle(type, rec.doc) : ''
  useTitle(title ? `History of ${title}` : 'History')

  if (error) return <ErrorState error={error} retry={refresh} />
  if (!data || !rec) return <Loading label="Loading history" />

  const restore = async (revision: number) => {
    setBusy(revision)
    setErr('')
    try {
      const old = await api<{ doc: AnyDoc }>(`/api/v1/${kind}/${id}/revisions/${revision}`)
      const edit = await api<{ id: string; status: string }>('/api/v1/edits', {
        method: 'POST',
        json: {
          entity_type: type,
          entity_id: id,
          action: 'update',
          base_revision: rec.revision,
          doc: old.doc,
          note: `Restore revision ${revision}`,
        },
      })
      invalidate(`/api/v1/${kind}/${id}`, '/api/v1/edits', '/api/v1/stats')
      navigate(`/edits/${edit.id}`)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not restore that revision')
      setBusy(null)
    }
  }

  return (
    <div className="page">
      <nav aria-label="Breadcrumb" className="crumbs">
        <ol>
          <li>
            <Link to={`/${kind}`}>{TYPE_LABELS[type].many}</Link>
          </li>
          <li>
            <Link to={`/${kind}/${id}`}>{title}</Link>
          </li>
        </ol>
      </nav>
      <h1 className="page-title">History</h1>
      <p className="lede">
        Every applied change to this record, newest first. Each links to its full diff and discussion.
      </p>
      {err && (
        <p className="notice notice-danger" role="alert">
          {err}
        </p>
      )}
      <ol className="timeline">
        {data.items.map((h) => (
          <li key={h.revision} className="timeline-item">
            <span className="timeline-rev">r{h.revision}</span>
            <div className="timeline-body">
              <p className="timeline-what">
                <Link to={`/edits/${h.edit_id}`}>
                  {h.revision === 1
                    ? 'Created'
                    : h.action === 'delete'
                      ? 'Deleted'
                      : `Changed ${h.fields.map(fieldLabel).join(', ') || 'details'}`}
                </Link>
              </p>
              {h.note && <p className="timeline-note">“{h.note}”</p>}
              <p className="timeline-meta">
                {h.author && (
                  <Link to={`/users/${h.author.id}`} className="person">
                    <Avatar name={h.author.name} src={h.author.avatar_url} size={18} /> {h.author.name}
                  </Link>
                )}
                <Time ms={h.created_at} />
                {h.reviewer ? (
                  <span>
                    approved by {h.reviewer.name}
                    {h.corrected ? ', with corrections' : ''}
                  </span>
                ) : h.auto_applied ? (
                  <span>applied directly</span>
                ) : null}
              </p>
            </div>
            {session.can('editor') && h.revision !== rec.revision && h.action !== 'delete' && (
              <button
                type="button"
                className="button button-quiet button-small"
                disabled={busy !== null}
                onClick={() => restore(h.revision)}
              >
                <Undo2 size={14} aria-hidden="true" /> {busy === h.revision ? 'Restoring…' : 'Restore'}
              </button>
            )}
          </li>
        ))}
      </ol>
    </div>
  )
}
